'use client';

// S4 P0 — 분양 정보 안내 신청 폼. (S4-2: 필드 4개 · 입력 예시 · 시인성 / S7-3: 문구 분리)
// leadform_ink1 (2026-10-18 · Node 11:48) — 화면 전면 교체 «Ink & White». 입력 칸 = 이름 · 연락처 · 생년월일(허브는 생년월일 없음).
//   «희망 타입» 칸은 화면에서 뺐다(Node 직접 지시 · 「필드 불변」 규칙의 유일한 예외). 저장 호환을 위해 desiredType 은 「미정」 고정 전송.
//   동의 2개(개인정보 필수 · 문자 수신 선택) · 허니팟(company) · 채널/광고 파라미터 · 이벤트 계측 · 전송 경로·재시도·미전송 보관은 종전 그대로다.
//   하단 mbar 「관심고객 등록」은 이 «같은 한 벌» 을 바텀시트로 옮겨 그린다(lib/apt/lead-sheet · 폼 인스턴스 1 유지).
// 서버(Apps Script 웹앱: 시트 기록 + 메일 알림 + Supabase 백업)는 이미 검증 완료 상태다.
//
// Content-Type 은 반드시 text/plain;charset=utf-8 이어야 한다.
// application/json 을 쓰면 브라우저가 OPTIONS preflight 를 먼저 보내는데 Apps Script 는
// OPTIONS 에 응답할 수 없어 CORS 로 전부 차단된다. text/plain 은 preflight 면제 대상이고
// 서버는 e.postData.contents 를 그대로 파싱하므로 문제없다.
// 로컬에서는 통과하고 배포 후에만 실패하는 유형이라 이 헤더는 바꾸지 말 것.
//
// mode:'no-cors' 도 쓰지 않는다 — 응답 본문을 못 읽게 되어 성공/실패 판별과 재시도가 무의미해진다.

import { useCallback, useEffect, useRef, useState } from 'react';
import type { CSSProperties, ChangeEvent, FormEvent, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import './lead-form.css';
import { SECTION_SCROLL_MARGIN } from '@/components/apt/SiteJumpBar';
import { SITE_URL } from '@/lib/constants';
import { leadCopy, leadCopyForHome, leadCopyForHub } from '@/lib/apt/lead-copy';
import { hubLeadPath } from '@/lib/apt/lead-eligibility';
import { trackLeadStart, trackLeadSubmit, trackLeadView } from '@/lib/apt/lead-track';
// CPL v5 §4 P4 — 네이버 검색광고 전환. 발화 조건·중복 규율은 그 파일 머리말에 있다.
import { fireNaverLeadConversion } from '@/lib/naver-conversion';
import { leadPilotArm } from '@/lib/apt/lead-pilot';
// CL fix_7 §1·§2 (레인 0b) — 유입 출처 캡처(kd_entry) · 제출 실패 계측.
import { buildLeadQuery } from '@/lib/ad-entry';
import { captureEntryClient, readStoredAttribution } from '@/lib/ad-entry/client';
import { track } from '@/lib/analytics';
import { buildLeadPayload, LEAD_BIRTH_REQUIRED, leadBirthError, leadInkCopy, leadInkKind, maskPhone, type LeadPayload, type LeadSummary } from '@/lib/apt/lead-ink';
import { LEAD_SHEET_EVENT } from '@/lib/apt/lead-sheet';

/* ⚠️ 값을 여기서 다시 읽지 않는다 — lead-eligibility 의 LEAD_ENDPOINT 한 곳이 원본이다.
   두 벌이면 한쪽만 고쳐져 「폼은 서는데 버튼이 없다」 같은 어긋남이 다시 난다(V4-D P0-A). */
import { LEAD_ENDPOINT as ENDPOINT } from '@/lib/apt/lead-eligibility';
import { LEAD_FORM_ID } from '@/lib/apt/detail-anchors';
import { KAKAO_TALK_URL, trackTalkClick, trackTalkView } from '@/lib/talk-banner';
const DRAFT_PREFIX = 'kd_lead_draft:';
const PENDING_PREFIX = 'kd_lead_pending:';
// 최초 1회 즉시 전송 후, 실패하면 이 간격만큼 쉬고 재시도 (총 3회 재시도)
const RETRY_DELAYS = [300, 900, 2700];

/**
 * 하단 전체 폼의 앵커 id — 하단 액션바·레일 진입 카드가 이 id 로 스크롤한다.
 * ⚠️ 값을 바꾸지 말 것.
 */
/* ⛔ 여기서 LEAD_FORM_ID 를 export 하지 않는다 — 이 파일은 'use client' 라
   서버가 그 export 를 읽으면 문자열이 아니라 클라이언트 참조 프록시를 받는다.
   상수는 lib/apt/detail-anchors.ts 가 든다(2026-08-30 · P0-A). */

type LeadFormProps = {
  /**
   * apt_sites.slug — leads ↔ apt_sites 조인 키.
   * ⚠️ H1-3: 홈(variant='home')에는 현장이 없어 optional 이다. `leads.site_slug` 는 nullable 이라
   *    빈 값으로 들어가도 된다(DB 담당 확인). 상세·블로그에서는 그대로 필수처럼 쓴다.
   */
  siteSlug?: string;
  siteName?: string; // apt_sites.name
  /** Q-8 — 정비사업 현장인가. false 면 공고 전 문구가 「이 현장 분양 소식」판. 모르면 비워 둔다. */
  isRedev?: boolean;
  /** 판정 ②′(ii) — 기축이면서 재건축 진행 중. 매물이 아니라 진행 상황을 묻는다. */
  redevInProgress?: boolean;
  /** @deprecated leadform_ink1 — 희망 타입 칸이 모든 변형에서 빠졌다(desiredType = 「미정」 고정). 호출 호환용으로만 받는다. */
  hideTypeSelect?: boolean;
  /** @deprecated leadform_ink1 — 희망 타입 칸 제거. 호출 호환용으로만 받는다(렌더·전송에 쓰지 않는다). */
  typeOptions?: string[];
  /**
   * v6-4: 어느 지역 현장인지. 시트를 훑을 때 이름만으로는 구분이 안 된다
   * (현장이 수백 개다). apt_sites.region/sigungu 채움률 98.2%.
   * ⚠️ 값이 없으면 빈 문자열로 보내고 폼을 막지 않는다 — 지역이 없다고 리드를 잃으면 안 된다.
   */
  region?: string | null;
  sigungu?: string | null;
  /**
   * 놓이는 자리. 'home'·'region' 은 가리킬 «현장» 이 없어 문구 한 벌을 따로 쓴다.
   * ⚠️ postLead·재시도·미전송 보관·동의·허니팟·계측은 넷이 «완전히 같은 함수» 를 쓴다.
   *    갈라지는 것은 문구와 보이는 칸뿐이다.
   */
  variant?: 'detail' | 'blog' | 'home' | 'region';
  /**
   * B-1: 허브가 가리키는 시·도 이름(`부산`). `variant='region'` 에서만 쓴다.
   * ⚠️ siteSlug 와 «다른 값» 이다. siteSlug 는 `redev:부산`(집계용 식별자),
   *    이건 사람이 읽는 지역명이자 siteUrl 조립의 원본이다.
   */
  regionName?: string;
  /** ONESHOT §C-1: 단계별 문구. 공고 전에 '분양 정보 안내' 는 어색하다. */
  lifecycleStage?: string | null;
  /**
   * C-4: 홈 폼의 「관심 지역」 선택지. `variant='home'` 에서만 쓰이고 **필수 입력**이 된다.
   * ⚠️ 원본은 `lib/apt/pipeline.ts` 의 BUGYEONG_REGIONS — 서버 컴포넌트(홈)가 읽어 prop 으로 내려 준다.
   */
  regionChoices?: readonly string[];
  /** leadform_ink1 — 일정 요약 3칸(분양가 · 특별공급 접수일 · 당첨 발표일) + 청약 D-n. 서버가 lib/apt/lead-ink leadSummaryOf 로 만든다. 없으면 줄 생략. */
  summary?: LeadSummary | null;
};

type FieldErrors = { name?: string; phone?: string; birthDate?: string; consent?: string; homeRegion?: string };

/** 첫 오류 칸으로 포커스할 때의 순서 — 화면 순서와 같다. */
const ERROR_ORDER: Array<[keyof FieldErrors, string]> = [
  ['name', 'kd-lead-name'], ['phone', 'kd-lead-phone'], ['homeRegion', 'kd-lead-region'], ['birthDate', 'kd-lead-birth'], ['consent', 'kd-lead-consent'],
];

const sleep = (ms: number) => new Promise<void>(resolve => setTimeout(resolve, ms));

const onlyDigits = (v: string) => v.replace(/[^0-9]/g, '');

function formatPhone(v: string) {
  const d = onlyDigits(v).slice(0, 11);
  if (d.length < 4) return d;
  if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
  if (d.length === 10) return `${d.slice(0, 3)}-${d.slice(3, 6)}-${d.slice(6)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
}

// 서버는 필터에 걸린 요청도 HTTP 200 + ok:true 로 답하고 skipped 사유만 덧붙인다
// (실측: {"ok":true,"skipped":"too_fast"}, {"ok":true,"skipped":"honeypot"}).
// ok:true 만 보고 성공 처리하면 걸러진 신청이 접수된 것처럼 안내된다 — skipped 를 반드시 본다.
//   recorded    실제로 기록됨
//   silent-drop 허니팟. 봇에게는 성공 화면을 보여주는 게 의도된 동작이다
//   rejected    서버가 거절. 같은 payload 로 재시도해도 결과가 같으므로 재시도하지 않는다
//   failed      네트워크/응답 실패. 재시도 대상
type PostOutcome = 'recorded' | 'silent-drop' | 'rejected' | 'failed';

/* CL fix_7 §2 — 마지막 'failed' 의 «이유». submit_fail 이벤트 속성으로만 쓴다(판정 로직은 그대로).
   fetch 예외는 호출부 catch 가 'fetch_error' 로 적는다. */
let lastPostFailReason: string | null = null;

async function postLead(payload: LeadPayload): Promise<PostOutcome> {
  lastPostFailReason = null;
  const res = await fetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'text/plain;charset=utf-8' },
    body: JSON.stringify(payload),
  });
  if (!res.ok) { lastPostFailReason = `http_${res.status}`; return 'failed'; }
  const json = (await res.json().catch(() => null)) as { ok?: boolean; skipped?: string } | null;
  if (json?.ok !== true) { lastPostFailReason = json ? 'not_ok' : 'bad_json'; return 'failed'; }
  if (!json.skipped) return 'recorded';
  return json.skipped === 'honeypot' ? 'silent-drop' : 'rejected';
}

/** localStorage 는 사파리 프라이빗 등에서 접근 자체가 throw 한다 — 전부 감싼다. */
function lsGet(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function lsSet(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* 저장 불가 환경 — 임시 보관만 포기하고 폼 자체는 정상 동작 */
  }
}
function lsRemove(key: string) {
  try {
    localStorage.removeItem(key);
  } catch {
    /* noop */
  }
}

// hc4 ⑧ · ds2 S2 — 「필수」「선택」 표식 굵기 400(globals.css form[data-rd-form=lead] span[data-rd=label] 덮개와 같은 값).
const badgeBase: CSSProperties = {
  fontSize: 12,
  fontWeight: 400,
  lineHeight: 1.4,
};

// 허니팟 — display:none 은 일부 봇이 건너뛰므로 화면 밖으로 밀어내는 방식을 쓴다.
const honeypotStyle: CSSProperties = {
  position: 'absolute',
  left: -9999,
  top: 0,
  width: 1,
  height: 1,
  opacity: 0,
  pointerEvents: 'none',
};

const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([tabindex="-1"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const CheckSvg = ({ w = 1.8 }: { w?: number }) => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={w} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d="M5 12.5 10 17.5 19 7" /></svg>
);

export default function LeadForm({
  siteSlug = '',
  siteName = '',
  region,
  sigungu,
  variant = 'detail',
  lifecycleStage,
  regionChoices,
  isRedev,
  redevInProgress,
  regionName,
  summary,
}: LeadFormProps) {
  const isHome = variant === 'home';
  // B-1 — 허브판. 단계가 없고, 가리키는 현장도 하나가 아니다.
  const isRegion = variant === 'region';
  // 홈인데 선택지가 안 내려왔으면 셀렉트를 필수로 걸 수 없다. 폼을 막는 대신
  // 지역 칸만 빠지고 나머지는 그대로 돈다 — 리드를 놓치는 쪽이 더 비싸다.
  const homeRegions = isHome ? (regionChoices ?? []) : [];
  const needsRegion = homeRegions.length > 0;
  // ONESHOT §C-1: 단계별 문구 한 벌. 세 화면(폼·액션바·레일)이 같은 말을 해야 한다.
  const copy = isHome
    ? leadCopyForHome()
    // B-2: 허브가 셋이 됐다(redev · region · area). 어느 허브인지는 site_slug 접두가 말한다.
    : isRegion
      ? leadCopyForHub(siteSlug, regionName ?? region ?? '')
      : leadCopy(lifecycleStage, siteName, { redev: isRedev, redevInProgress });
  // leadform_ink1 — 화면 문구(눈썹·제목·안내·완료)는 lib/apt/lead-ink 한 벌. inquiryType 은 종전 copy 그대로.
  const inkKind = leadInkKind({ variant, lifecycleStage, isRedev, redevInProgress, copy });
  const ink = leadInkCopy({ kind: inkKind, copy, siteName, regionName: regionName ?? region ?? '', dday: summary?.dday ?? null });
  const sumCells = summary?.cells ?? [];
  const mountedAt = useRef(Date.now());

  // E-12 — 선택 필드 파일럿. 군은 현장 slug 해시(홈·허브는 밖). 칸은 Node 21:15(2026-09-26)에 모든 변형에서 제거 —
  //   군 배정(pilotArm)은 계측 속성 pilot_arm 으로만 남기고, pilot_fields 는 항상 false(칸이 없다).
  const pilotArm = isHome || isRegion ? null : leadPilotArm(siteSlug);
  const pilotOn = false;
  const startedRef = useRef(false);
  const markStart = () => {
    if (startedRef.current) return;
    startedRef.current = true;
    trackLeadStart('body', { site_slug: siteSlug, lifecycle_stage: lifecycleStage, pilot_arm: pilotArm ?? undefined, pilot_fields: pilotArm === 'exp' && pilotOn, entry_src: captureEntryClient()?.src ?? 'organic' });
  };

  // §5-3: 본문 폼이 **실제로 화면에 들어온 순간**만 노출로 센다.
  // ⚠️ 마운트 시점에 세지 않는다 — 폼은 페이지 하단에 있어 대부분의 방문자는 보지 못한다.
  //    마운트로 세면 분모가 부풀어 전환율이 실제보다 낮게 나온다.
  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return;
    const el = document.getElementById(LEAD_FORM_ID);
    if (!el) return;
    let fired = false;
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (!e.isIntersecting || fired) continue;
          fired = true;
          trackLeadView('body', { site_slug: siteSlug, lifecycle_stage: lifecycleStage, pilot_arm: pilotArm ?? undefined });
          io.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [siteSlug, lifecycleStage]);
  // ⚠️ 홈은 siteSlug 가 없다. 빈 문자열을 그대로 쓰면 키가 `kd_lead_draft:` 가 되어
  //    나중에 다른 무슬러그 변형이 생기면 초안이 섞인다. 자리 이름으로 namespace 를 준다.
  const storageKey = siteSlug || (isHome ? 'home' : 'unknown');
  const draftKey = `${DRAFT_PREFIX}${storageKey}`;
  const pendingKey = `${PENDING_PREFIX}${storageKey}`;

  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [birthDate, setBirthDate] = useState('');
  // C-4: 홈 폼의 관심 지역. 상세·블로그는 region prop 이 있어 쓰지 않는다.
  const [homeRegion, setHomeRegion] = useState('');
  const [consent, setConsent] = useState(false);
  const [marketingOk, setMarketingOk] = useState(false);
  const [company, setCompany] = useState(''); // 허니팟
  const [errors, setErrors] = useState<FieldErrors>({});
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // 접수 건 식별 키. 폼 인스턴스당 1개. (2차 전송 복구 시 그대로 쓴다)
  const leadRefRef = useRef<string>('');
  if (!leadRefRef.current) {
    leadRefRef.current =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `lr_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
  }

  // 복원이 끝나기 전에 저장 effect 가 돌면 빈 값으로 초안을 덮어쓴다.
  const restored = useRef(false);
  const phoneRef = useRef<HTMLInputElement>(null);
  // 서식을 다시 입히면 캐럿이 끝으로 튄다 — 재렌더 후 복원할 "앞쪽 숫자 개수"를 담아둔다.
  const caretDigitsRef = useRef<number | null>(null);

  // Q-7 — 하이드레이션 «전» 에 친 값. 느린 회선·인앱 브라우저에서는 HTML 이 먼저 보여 사용자가 바로 친다.
  //   그 값은 DOM 에만 있고 state 는 '' 라서, 첫 재렌더(제출·오류 표시)에서 입력이 «지워진다»
  //   (2026-09-14 재현: 3G+CPU×4 에서 이름·연락처를 친 뒤 제출 → 빈칸 + 「이름을 입력해 주세요」).
  const formRef = useRef<HTMLFormElement>(null);

  // 임시 보관 복원
  useEffect(() => {
    // ⚠️ 초안보다 «먼저» 읽어 두고 «나중에» 덮는다 — 방금 사람이 친 값이 예전 초안보다 새 값이다.
    const f = formRef.current;
    const domVal = (id: string) => (f?.querySelector<HTMLInputElement | HTMLSelectElement>(`#${id}`)?.value ?? '');
    const domChecked = (key: string) => !!f?.querySelector<HTMLInputElement>(`input[data-kd="${key}"]`)?.checked;
    const typed = {
      name: domVal('kd-lead-name'),
      phone: domVal('kd-lead-phone'),
      birthDate: domVal('kd-lead-birth'),
      homeRegion: domVal('kd-lead-region'),
      consent: domChecked('consent'),
      marketingOk: domChecked('marketing'),
      company: f?.querySelector<HTMLInputElement>('input[name="company"]')?.value ?? '',
    };

    const raw = lsGet(draftKey);
    if (raw) {
      try {
        const d = JSON.parse(raw) as Partial<LeadPayload>;
        if (typeof d.name === 'string') setName(d.name);
        if (typeof d.phone === 'string') setPhone(d.phone);
        if (typeof d.birthDate === 'string') setBirthDate(d.birthDate);
        // C-4: 초안의 지역이 지금 내려온 선택지에 없으면 버린다 — 셀렉트에 없는 값이
        //      state 에 남으면 화면은 빈칸인데 검증은 통과하는 상태가 된다.
        {
          const draftRegion = (d as { homeRegion?: unknown }).homeRegion;
          if (typeof draftRegion === 'string' && homeRegions.includes(draftRegion)) {
            setHomeRegion(draftRegion);
          }
        }
        if (typeof d.consent === 'boolean') setConsent(d.consent);
        if (typeof d.marketingOk === 'boolean') setMarketingOk(d.marketingOk);
      } catch {
        lsRemove(draftKey);
      }
    }
    if (typed.name) setName(typed.name);
    if (typed.phone) setPhone(formatPhone(typed.phone));
    if (typed.birthDate) setBirthDate(onlyDigits(typed.birthDate).slice(0, 6));
    if (typed.homeRegion && homeRegions.includes(typed.homeRegion)) setHomeRegion(typed.homeRegion);
    if (typed.consent) setConsent(true);
    if (typed.marketingOk) setMarketingOk(true);
    // 허니팟도 같은 규칙 — 봇이 하이드레이션 전에 채웠다고 통과시키지 않는다.
    if (typed.company) setCompany(typed.company);
    restored.current = true;
  }, [draftKey]);

  // 입력값이 바뀔 때마다 임시 보관. 삭제는 전송 성공 응답을 받은 뒤에만 한다.
  useEffect(() => {
    if (!restored.current) return;
    lsSet(draftKey, JSON.stringify({ name, phone, birthDate, homeRegion, consent, marketingOk }));
  }, [draftKey, name, phone, birthDate, homeRegion, consent, marketingOk]);

  // 서식 적용 후 캐럿을 원래 자리로 되돌린다 (앞쪽 숫자 개수를 기준으로 위치를 다시 찾는다).
  useEffect(() => {
    const want = caretDigitsRef.current;
    const el = phoneRef.current;
    caretDigitsRef.current = null;
    if (want === null || !el) return;
    let pos = 0;
    let seen = 0;
    while (pos < el.value.length && seen < want) {
      if (/[0-9]/.test(el.value[pos])) seen++;
      pos++;
    }
    el.setSelectionRange(pos, pos);
  }, [phone]);

  // 미전송분 재시도 — 조용히 1회. 실패해도 UI 에 아무것도 표시하지 않는다.
  useEffect(() => {
    let cancelled = false;
    const keys: string[] = [];
    try {
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && k.startsWith(PENDING_PREFIX)) keys.push(k);
      }
    } catch {
      return;
    }
    if (keys.length === 0) return;

    (async () => {
      for (const k of keys) {
        if (cancelled) return;
        const raw = lsGet(k);
        if (!raw) continue;
        let payload: LeadPayload;
        try {
          payload = JSON.parse(raw) as LeadPayload;
        } catch {
          lsRemove(k); // 깨진 값은 영구히 남지 않게 정리
          continue;
        }
        try {
          // 'failed' 가 아니면 서버가 판정을 내린 것이므로 키를 정리한다.
          // 거절된 payload 를 남겨두면 매 마운트마다 재전송되는 영구 잔존 키가 된다.
          if ((await postLead(payload)) !== 'failed') lsRemove(k);
        } catch {
          /* 조용히 무시 — 다음 마운트에서 다시 시도한다 */
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  /* ── leadform_ink1 바텀시트 ──────────────────────────────────────────────
     하단 mbar 「관심고객 등록」(LeadJump bottom_bar)이 LEAD_SHEET_EVENT 를 보낸다 → 이 한 벌이 시트로 옮겨 그려진다.
     ⚠️ 폼은 «둘이 되지 않는다» — 시트가 열린 동안 본문 자리는 같은 높이의 빈 칸(kd-lf-hold)만 남는다.
        높이를 지키므로 페이지 스크롤 위치는 0 만큼 바뀐다(scrolltop1 과 독립 · window.scrollTo 를 부르지 않는다).
     ⚠️ 상태(입력값·오류·완료)는 이 컴포넌트에 있어 자리를 옮겨도 그대로다.
     닫기 = 닫기 버튼 · Esc · 스크림 · 뒤로가기(history state 1칸). 열린 동안 html 스크롤 잠금 · 포커스 가두기. */
  const [sheet, setSheet] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [holdH, setHoldH] = useState<number | null>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);
  const openerRef = useRef<HTMLElement | null>(null);
  const pushedRef = useRef(false);
  const sheetOpenRef = useRef(false);
  sheetOpenRef.current = sheet;
  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    if (!ENDPOINT) return;
    const onOpen = (e: Event) => {
      e.preventDefault(); // «받았다» — 호출부는 앵커 이동을 하지 않는다
      if (sheetOpenRef.current) return;
      openerRef.current = (document.activeElement as HTMLElement | null) ?? null;
      setHoldH(cardRef.current ? Math.round(cardRef.current.getBoundingClientRect().height) : null);
      setSheet(true);
    };
    window.addEventListener(LEAD_SHEET_EVENT, onOpen);
    return () => window.removeEventListener(LEAD_SHEET_EVENT, onOpen);
  }, []);

  const requestClose = useCallback(() => {
    if (!sheetOpenRef.current) return;
    if (pushedRef.current) {
      // 시트가 쌓은 history 1칸을 되돌린다 → popstate 가 닫는다(뒤로가기와 같은 길).
      try { window.history.back(); } catch { pushedRef.current = false; setSheet(false); return; }
      window.setTimeout(() => { if (sheetOpenRef.current) { pushedRef.current = false; setSheet(false); } }, 400);
      return;
    }
    setSheet(false);
  }, []);

  useEffect(() => {
    if (!sheet) return;
    const html = document.documentElement;
    const prev = { overflow: html.style.overflow, paddingRight: html.style.paddingRight };
    const gutter = window.innerWidth - html.clientWidth;
    html.style.overflow = 'hidden';
    if (gutter > 0) html.style.paddingRight = `${gutter}px`;
    try {
      window.history.pushState({ ...(window.history.state ?? {}), kdLeadSheet: 1 }, '');
      pushedRef.current = true;
    } catch { pushedRef.current = false; }
    const onPop = () => { pushedRef.current = false; setSheet(false); };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); requestClose(); return; }
      if (e.key !== 'Tab') return;
      const root = sheetRef.current;
      if (!root) return;
      const f = Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter((el) => el.getClientRects().length > 0);
      if (f.length === 0) { e.preventDefault(); root.focus({ preventScroll: true }); return; }
      const first = f[0];
      const last = f[f.length - 1];
      const a = document.activeElement as HTMLElement | null;
      if (!a || !root.contains(a)) { e.preventDefault(); first.focus(); return; }
      if (e.shiftKey && (a === first || a === root)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && a === last) { e.preventDefault(); first.focus(); }
    };
    window.addEventListener('popstate', onPop);
    document.addEventListener('keydown', onKey);
    const raf = window.requestAnimationFrame(() => { try { sheetRef.current?.focus({ preventScroll: true }); } catch { /* 무시 */ } });
    return () => {
      window.cancelAnimationFrame(raf);
      window.removeEventListener('popstate', onPop);
      document.removeEventListener('keydown', onKey);
      html.style.overflow = prev.overflow;
      html.style.paddingRight = prev.paddingRight;
      const o = openerRef.current;
      openerRef.current = null;
      try { o?.focus?.({ preventScroll: true }); } catch { /* 무시 */ }
    };
  }, [sheet, requestClose]);

  /**
   * 하이픈을 실시간으로 붙이되, 하이픈만 지워지는 상황을 따로 잡는다.
   * `010-1234-5678` 에서 하이픈 위 백스페이스는 숫자를 그대로 둔 채 길이만 줄이므로
   * 그냥 다시 서식을 입히면 하이픈이 되살아나 "지워지지 않는" 것처럼 보인다.
   * 이 경우 캐럿 앞 숫자 하나를 대신 지운다.
   */
  function handlePhoneChange(e: ChangeEvent<HTMLInputElement>) {
    const raw = e.target.value;
    const caret = e.target.selectionStart ?? raw.length;
    const prevDigits = onlyDigits(phone);
    let digits = onlyDigits(raw);
    let before = onlyDigits(raw.slice(0, caret)).length;

    if (raw.length < phone.length && digits.length === prevDigits.length && before > 0) {
      digits = digits.slice(0, before - 1) + digits.slice(before);
      before -= 1;
    }

    caretDigitsRef.current = before;
    setPhone(formatPhone(digits));
    if (errors.phone) setErrors(prev => ({ ...prev, phone: undefined }));
  }

  // v6-1: 이름·연락처·생년월일·동의를 본다.
  //   생년월일은 청약 자격·가점 확인에 바로 쓰이므로 필수다.
  const validate = useCallback((): FieldErrors => {
    const next: FieldErrors = {};
    if (!name.trim()) next.name = '이름을 입력해 주세요';
    if (onlyDigits(phone).length < 11) next.phone = '휴대폰 번호 11자리를 입력해 주세요';
    /* ⚠️ B-1 허브는 생년월일을 «묻지 않는다» — 그래서 검사도 하지 않는다.
       생년월일이 필수인 이유는 «청약 자격·가점» 확인이고, 그건 모집공고가 있는
       현장 이야기다. 허브가 약속하는 것은 「단계가 바뀌면 담당자가 연락」뿐이라
       받을 이유 없는 주민등록 정보를 필수로 걸면 약속과 실제가 어긋난다(규율 4).
       ⛔ 칸을 숨기면서 검사를 남기면 «채울 수 없는 칸 때문에 제출이 막힌다» —
          보이지 않는 에러로 폼이 죽는 전형이다. 둘은 반드시 같이 움직인다. */
    // retention-1031 — 생년월일은 선택(LEAD_BIRTH_REQUIRED · lib/apt/lead-ink). 비었거나 6자리면 통과.
    if (!isRegion) { const be = leadBirthError(birthDate); if (be) next.birthDate = be; }
    // C-4: 홈은 가리킬 현장이 없다. 지역까지 비면 담당자가 전화를 걸 근거가 없어진다.
    if (needsRegion && !homeRegion.trim()) next.homeRegion = '관심 지역을 선택해 주세요';
    if (!consent) next.consent = '필수 동의에 체크해 주세요';
    return next;
  }, [name, phone, birthDate, consent, needsRegion, homeRegion, isRegion]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (sending) return;

    const v = validate();
    if (Object.keys(v).length > 0) {
      setErrors(v);
      // 첫 오류 칸으로 포커스 — 스크롤은 그 칸만(가까운 쪽으로 최소 이동).
      const first = ERROR_ORDER.find(([k]) => v[k]);
      if (first) {
        window.requestAnimationFrame(() => {
          const el = document.getElementById(first[1]);
          if (!el) return;
          try { el.focus({ preventScroll: true }); el.scrollIntoView({ block: 'nearest' }); } catch { el.focus(); }
        });
      }
      return;
    }

    setSendError(null);
    setSending(true);

    /* CL fix_7 §1 — 유입 출처. 착지 페이지가 아닌 곳에서 제출해도 광고 파라미터가 살아 있게
       착지 때 보관한 값을 query 로 «복원» 한다(현재 URL 에 광고·utm 이 하나라도 있으면 현재 URL 만 쓴다).
       ⚠️ 새 필드를 만들지 않는다 — Apps Script 가 channel 을 판정하는 실제 입력은 query 다
          (n_* → naver_ad · utm_source → utm:x · 그 외 organic, leads 60행 실측). 출처 표식 kd_entry 도
          query 에 실어 leads.utm 에 남긴다. channel 필드는 종전대로 홈만 싣는다. */
    const entry = captureEntryClient();
    const leadQuery = buildLeadQuery(
      Object.fromEntries(new URLSearchParams(window.location.search)),
      readStoredAttribution(),
      entry,
    );

    // 홈은 사용자가 고른 값이 곧 관심 지역이다. 상세·블로그는 현장의 region 을 그대로 쓴다.
    const regionText = (isHome ? homeRegion : (region ?? '')).trim();
    const sigunguText = (isHome ? '' : (sigungu ?? '')).trim();

    // leadform_ink1 — 객체 조립은 lib/apt/lead-ink buildLeadPayload 한 곳(테스트가 같은 함수로 모양을 잠근다).
    //   desiredType = 「미정」 고정(희망 타입 칸 제거 · leads·시트 열 호환). 나머지 키·값은 종전과 같다.
    const payload: LeadPayload = buildLeadPayload({
      name,
      phone,
      birthDate,
      consent,
      marketingOk,
      dwellMs: Date.now() - mountedAt.current,
      siteSlug,
      siteName,
      regionText,
      sigunguText,
      // 시트에서 바로 열 수 있게 절대 URL 로. 판정은 constants 한 곳(SITE_URL)만 쓴다.
      /* ⚠️ 허브는 «현장 URL 이 아니다» — 경로는 hubLeadPath(siteSlug) 한 곳에서 낸다(인코딩 포함). */
      siteUrl: isRegion
        ? (() => { const p = hubLeadPath(siteSlug); return p ? `${SITE_URL.replace(/\/$/, '')}${p}` : ''; })()
        : siteSlug ? `${SITE_URL.replace(/\/$/, '')}/apt/${encodeURIComponent(siteSlug)}` : '',
      // ONESHOT §C-1: 공고 전은 '진행상황알림' 이다. 미처리 경보가 '분양상담' 으로 대상을 좁힌다.
      inquiryType: copy.inquiryType,
      entryPath: window.location.pathname,
      sourceDomain: window.location.hostname,
      // ⚠️ channel 은 홈에서만 싣는다(buildLeadPayload 안). 상세·블로그는 서버가 판정한다.
      isHome,
      company,
      // CL fix_7 §1 — 현재 URL 쿼리 + (없을 때만) 착지 때 보관한 광고·utm 파라미터 + kd_entry.
      query: leadQuery,
      leadRef: leadRefRef.current,
    });

    let outcome: PostOutcome = 'failed';
    let failReason: string | null = null;
    let attempts = 0;
    for (let attempt = 0; attempt <= RETRY_DELAYS.length; attempt++) {
      if (attempt > 0) await sleep(RETRY_DELAYS[attempt - 1]);
      attempts = attempt + 1;
      try {
        outcome = await postLead(payload);
        failReason = outcome === 'failed' ? lastPostFailReason : null;
      } catch (err) {
        outcome = 'failed';
        failReason = `fetch_error:${err instanceof Error ? err.name : 'unknown'}`;
      }
      if (outcome !== 'failed') break; // 서버가 판정을 내렸으면 재시도는 의미가 없다
    }

    setSending(false);

    /* CL fix_7 §2 — 제출 실패(fetch 예외·Apps Script 비정상 응답, 재시도 소진 뒤)를 따로 남긴다.
       lead_form_submit(outcome='failed')도 아래에서 종전대로 찍힌다 — 이것은 «끊김 경보» 전용 축이다
       (sql/cl/0b/lead_break_alert 가 event_type='submit_fail' 48h 3회↑ 를 critical 로 올린다).
       ⚠️ 개인정보를 싣지 않는다 — 이름·전화·생년월일은 이벤트에 들어가지 않는다. */
    if (outcome === 'failed') {
      track('submit_fail', 'apt_lead_form', {
        site_slug: siteSlug, lifecycle_stage: lifecycleStage, reason: failReason ?? 'unknown', attempts,
        entry_src: entry?.src ?? 'organic',
      });
    }

    /* CPL C-2 — 제출은 «결과와 무관하게» 한 번 찍는다. 결과를 속성으로 실으면 한 쿼리로 갈린다.
     * ⚠️ 전환 집계는 `outcome='recorded'` 로 «걸러서» 낸다 — silent-drop(허니팟=봇)을 전환에 넣지 않는다(§5-3).
     * CPL v5 §4 P4 — 네이버 검색광고 전환. `recorded` 일 «때만» 쏜다(rejected·failed·silent-drop 은 전환이 아니다).
     * ⚠️ trackLeadSubmit «앞» 에 둔다 — 발화 결과를 그 이벤트에 «속성으로» 싣는다. 동기 함수다. */
    const naverCnv = outcome === 'recorded' ? fireNaverLeadConversion(leadRefRef.current) : null;

    trackLeadSubmit('body', {
      site_slug: siteSlug, lifecycle_stage: lifecycleStage, outcome,
      pilot_arm: pilotArm ?? undefined, pilot_fields: pilotArm === 'exp' && pilotOn,
      naver_cnv: naverCnv ?? undefined,
      entry_src: entry?.src ?? 'organic',
    });

    if (outcome === 'recorded' || outcome === 'silent-drop') {
      lsRemove(draftKey);
      lsRemove(pendingKey);
      // ⚠️ silent-drop 은 허니팟(봇)이다 — 성공 화면은 보여주되 전환에는 넣지 않는다.
      setDone(true);
      return;
    }

    // 접수된 것처럼 안내하지 않는다.
    // rejected 는 재전송해도 같은 결과라 보관하지 않고, failed 만 다음 방문에 조용히 재전송한다.
    if (outcome === 'failed') lsSet(pendingKey, JSON.stringify(payload));
    setSendError('전송에 실패했습니다. 잠시 후 다시 시도해 주세요.');
  }

  // 엔드포인트가 없으면 폼만 덩그러니 보이고 제출이 실패하는 상태가 되므로 아예 렌더하지 않는다.
  if (!ENDPOINT) return null;

  /* «Ink & White» 카드 한 벌. inSheet 면 같은 내용을 시트 안에 테두리 없이 그린다(제목은 시트 머리가 대신).
     ⛔ 폼 제목은 «시각 라벨» 이다(h 태그 아님) — DT-6 ①: 문서 아웃라인에 상업 문구를 올리지 않는다. */
  const renderCard = (inSheet: boolean): ReactNode => {
    const cls = inSheet ? 'kd-lf kd-lf--sheet' : 'kd-lf';
    // s-v2: 성공 화면의 '추가 정보 보내기'(2차 POST)는 제거했다 — dedupe_key 재문의 카운터 오염(LeadPayload.leadRef 주석 · lib/apt/lead-ink).
    if (done) {
      /* PLS 확장소재 §1 (2026-09-26) — id={LEAD_FORM_ID} 는 호출부의 항상-렌더 wrapper 가 하나만 가진다(중복 id 방지). */
      return (
        <div ref={inSheet ? undefined : cardRef} className={cls} style={{ scrollMarginTop: SECTION_SCROLL_MARGIN }} data-lf-state="done">
          <div className="kd-lf-done" role="status">
            <span className="kd-lf-done-ic"><CheckSvg w={1.8} /></span>
            <p className="kd-lf-done-h">신청이 완료됐어요</p>
            <p className="kd-lf-done-p">{ink.doneLine(maskPhone(phone))}</p>
            <hr className="kd-lf-rule" />
            <div className="kd-lf-acts">
              {/* 세션 A 19:35 — 현장 상세(variant='detail')의 접수 완료 화면에만 카톡방 버튼 1개.
                  홈·허브 폼은 대상이 아니다. 접수 «뒤» 에만 보이므로 폼 전환을 가로채지 않는다.
                  done 은 클라이언트 상태라 SSR HTML 에는 나오지 않는다(클로킹 표본 무관). */}
              {!isHome && !isRegion && (
                <LeadDoneTalkCta siteSlug={siteSlug} />
              )}
              <a className="kd-lf-btn kd-lf-btn--line" href="/calendar">청약 일정 달력 보기</a>
            </div>
          </div>
        </div>
      );
    }

    /* PLS 확장소재 §1 — 위 done-분기와 같은 이유로 id 없음(중복 id 방지). */
    return (
      <div ref={inSheet ? undefined : cardRef} className={cls} style={{ scrollMarginTop: SECTION_SCROLL_MARGIN }} data-lf-state={Object.values(errors).some(Boolean) ? 'error' : 'default'}>
        <div className="kd-lf-head">
          <p className="kd-lf-eb">{ink.eyebrow}</p>
          <div className="kd-lf-title">{ink.titleName}<span {...(String(ink.titleSub ?? '').length > 24 ? { 'data-long': '' } : {})}>{ink.titleSub}</span></div>
        </div>

        {sumCells.length >= 2 && (
          <dl className="kd-lf-sum" data-lf-summary="">
            {sumCells.map((c) => (
              <div key={c.key}><dt>{c.label}</dt><dd>{c.value}</dd></div>
            ))}
          </dl>
        )}

        <form data-rd-form="lead" ref={formRef} className="kd-lf-form" onSubmit={handleSubmit} onFocusCapture={markStart} noValidate>
          <div className="kd-lf-field">
            <label htmlFor="kd-lead-name" className="kd-lf-label">이름</label>
            <input
              id="kd-lead-name"
              className="kd-lf-in"
              type="text"
              value={name}
              onChange={e => {
                setName(e.target.value);
                if (errors.name) setErrors(prev => ({ ...prev, name: undefined }));
              }}
              placeholder="홍길동"
              autoComplete="name"
              aria-invalid={!!errors.name}
              aria-describedby={errors.name ? 'kd-lead-name-err' : undefined}
            />
            {errors.name && <p id="kd-lead-name-err" role="alert" className="kd-lf-err">{errors.name}</p>}
          </div>

          <div className="kd-lf-field">
            <label htmlFor="kd-lead-phone" className="kd-lf-label">연락처</label>
            <input
              id="kd-lead-phone"
              className="kd-lf-in"
              ref={phoneRef}
              type="tel"
              inputMode="numeric"
              value={phone}
              onChange={handlePhoneChange}
              placeholder="010-1234-5678"
              autoComplete="tel"
              maxLength={13}
              aria-invalid={!!errors.phone}
              aria-describedby={errors.phone ? 'kd-lead-phone-err' : undefined}
            />
            {errors.phone && <p id="kd-lead-phone-err" role="alert" className="kd-lf-err">{errors.phone}</p>}
          </div>

          {/* C-4 관심 지역 — 홈에서만, 그리고 필수다. 상세·블로그에는 렌더하지 않는다(현장이 곧 지역). */}
          {needsRegion && (
            <div className="kd-lf-field">
              <label htmlFor="kd-lead-region" className="kd-lf-label">관심 지역</label>
              <select
                id="kd-lead-region"
                className="kd-lf-in"
                aria-invalid={!!errors.homeRegion}
                aria-describedby={errors.homeRegion ? 'kd-lead-region-err' : undefined}
                value={homeRegion}
                onChange={e => {
                  setHomeRegion(e.target.value);
                  if (errors.homeRegion) setErrors(prev => ({ ...prev, homeRegion: undefined }));
                }}
              >
                <option value="">선택해 주세요</option>
                {homeRegions.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>
              {errors.homeRegion
                ? <p id="kd-lead-region-err" role="alert" className="kd-lf-err">{errors.homeRegion}</p>
                : <p className="kd-lf-hint">담당자가 해당 지역 현장으로 안내해 드립니다</p>}
            </div>
          )}

          {/* ⚠️ B-1 허브에서는 생년월일 칸을 «그리지 않는다» — 청약 자격·가점 확인용이고 허브에는 모집공고가 없다.
               state·payload·DB 열은 그대로(birthDate 는 빈 문자열로 실려 간다 — null 처리는 서버). */}
          {!isRegion && (
            <div className="kd-lf-field">
              <label htmlFor="kd-lead-birth" className="kd-lf-label">생년월일 6자리{LEAD_BIRTH_REQUIRED ? null : <span className="kd-lf-opt"> (선택)</span>}</label>
              <input
                id="kd-lead-birth"
                className="kd-lf-in"
                type="text"
                inputMode="numeric"
                value={birthDate}
                onChange={e => {
                  setBirthDate(onlyDigits(e.target.value).slice(0, 6));
                  if (errors.birthDate) setErrors(prev => ({ ...prev, birthDate: undefined }));
                }}
                placeholder="920502"
                maxLength={6}
                autoComplete="bday"
                aria-invalid={!!errors.birthDate}
                aria-describedby={errors.birthDate ? 'kd-lead-birth-err' : undefined}
              />
              {errors.birthDate && <p id="kd-lead-birth-err" role="alert" className="kd-lf-err">{errors.birthDate}</p>}
            </div>
          )}

          {/* 허니팟 — 사람에게는 보이지 않는다 */}
          <input
            type="text"
            name="company"
            value={company}
            onChange={e => setCompany(e.target.value)}
            tabIndex={-1}
            autoComplete="off"
            aria-hidden="true"
            style={honeypotStyle}
          />

          {/* 동의 두 줄 — 행 높이 44(.kd-lf-row · .kd-lf-check min-height 44) · 커스텀 체크 20 · label 이 행 전체 타깃.
              「보기」 링크(/privacy)도 44×44. data-kd 는 종전 그대로(Q-7 하이드레이션 전 입력 복원이 data-kd 로 읽는다). */}
          <div className="kd-lf-consents">
            <div className="kd-lf-row">
              <label className="kd-lf-check">
                <input
                  id="kd-lead-consent"
                  type="checkbox"
                  data-kd="consent"
                  checked={consent}
                  aria-invalid={!!errors.consent}
                  aria-describedby={errors.consent ? 'kd-lead-consent-err' : undefined}
                  onChange={e => {
                    setConsent(e.target.checked);
                    if (errors.consent) setErrors(prev => ({ ...prev, consent: undefined }));
                  }}
                />
                <span className="kd-lf-box" aria-hidden="true"><CheckSvg w={2.4} /></span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 'var(--fs-meta-rd3, 13px)', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                  개인정보 수집·이용 동의
                  <span data-rd="label" className="kd-lf-req" style={badgeBase}>필수</span>
                </span>
              </label>
              <a href="/privacy" target="_blank" rel="noopener noreferrer" className="kd-lf-view" aria-label="개인정보 수집·이용 동의 내용 보기">보기</a>
            </div>
            {errors.consent && <p id="kd-lead-consent-err" role="alert" className="kd-lf-err">{errors.consent}</p>}

            <div className="kd-lf-row">
              <label className="kd-lf-check">
                <input
                  type="checkbox"
                  data-kd="marketing"
                  checked={marketingOk}
                  onChange={e => setMarketingOk(e.target.checked)}
                />
                <span className="kd-lf-box" aria-hidden="true"><CheckSvg w={2.4} /></span>
                <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: 'var(--fs-meta-rd3, 13px)', color: 'var(--text-secondary)', lineHeight: 1.4 }}>
                  분양 정보 문자 수신 동의
                  <span data-rd="label" className="kd-lf-opt" style={badgeBase}>선택</span>
                </span>
              </label>
            </div>
          </div>

          <button type="submit" disabled={sending} className="kd-lf-submit">
            {sending ? '전송 중…' : (
              <>
                알림 신청하기
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false"><path d="M5 12h14M13 6l6 6-6 6" /></svg>
              </>
            )}
          </button>

          {sendError && <p role="alert" className="kd-lf-senderr">{sendError}</p>}
        </form>

        <p className="kd-lf-note">{ink.note}<br />{ink.note2}</p>
      </div>
    );
  };

  const sheetNode = sheet && mounted ? createPortal(
    <div className="kd-lf-ov" onClick={requestClose} data-lf-sheet="">
      <div
        ref={sheetRef}
        className="kd-lf-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby="kd-lf-sheet-title"
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="kd-lf-grip" aria-hidden="true" />
        <div className="kd-lf-shead">
          <p id="kd-lf-sheet-title" className="kd-lf-stitle">{ink.sheetTitle}</p>
          <button type="button" className="kd-lf-x" onClick={requestClose} aria-label="닫기">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" aria-hidden="true" focusable="false"><path d="M6 6l12 12M18 6 6 18" /></svg>
          </button>
        </div>
        <div className="kd-lf-sbody">{renderCard(true)}</div>
      </div>
    </div>,
    document.body,
  ) : null;

  return (
    <>
      {sheet && mounted
        ? <div className="kd-lf-hold" aria-hidden="true" style={holdH ? { height: holdH } : undefined} />
        : renderCard(false)}
      {sheetNode}
    </>
  );
}

/** 접수 완료 화면의 카톡방 버튼 — utm_source=leadform_done, 슬롯 'leadform_done'. */
export const LEAD_DONE_TALK_HREF = `${KAKAO_TALK_URL}?utm_source=leadform_done`;

function LeadDoneTalkCta({ siteSlug }: { siteSlug: string }) {
  useEffect(() => {
    trackTalkView('leadform_done', { site_slug: siteSlug || undefined });
  }, [siteSlug]);
  return (
    <a
      href={LEAD_DONE_TALK_HREF}
      target="_blank"
      rel="noopener noreferrer"
      data-talk-slot="leadform_done"
      data-slot="leadform_done"
      onClick={() => trackTalkClick('leadform_done', { site_slug: siteSlug || undefined })}
      className="kd-lf-btn kd-lf-btn--ink"
    >
      카톡방에서 바로 물어보기
    </a>
  );
}
